import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { cx } from "@nju/css";
import { useOffers } from "@nju/data-access-portal-offers";
import { useUserLoginState } from "@nju/data-access-portal-user";
import { useDecodedToken } from "@nju/data-access-portal-wwt";
import { redirectToOrchestratorState } from "@nju/feature-portal-orchestrator";
import { portalRoutes, salesRoutes } from "@nju/routes";
import { IScope } from "@nju/scope";
import {
  A,
  BlockText,
  CircleLoader,
  GeneralErrorNotification,
  Heading,
  Separator,
  Text,
} from "@nju/ui";
import { formatAddress, getSessionAddress, IAddress } from "@nju/utils";
import { selectOfferMachine } from "../selectOffer.machine";
import { generateInstallationStandardTexts } from "../utils";
import { Offers } from "./partials";

interface ISelectOfferFibercoPage {
  variant?: IScope;
}

export function SelectOfferFibercoPage({
  variant = "portal",
}: ISelectOfferFibercoPage) {
  const { isLoggedIn } = useUserLoginState();
  const { indefiniteOffers } = useOffers().ftth;
  const router = useRouter();
  const installationToken = sessionStorage.getItem("installationToken");
  const decodedToken = useDecodedToken(installationToken);
  const currentActiveCLient = localStorage.getItem("current_client_email");
  const generatedTextFromInstallationToken = generateInstallationStandardTexts({
    installationStandard: decodedToken?.address.installationStandard,
    isSfh: decodedToken?.address?.isSfh,
    variant,
  });

  const filteredOffers = React.useMemo(() => {
    return [...indefiniteOffers]
      .filter((offer) => {
        const matchesLimited = decodedToken?.limited
          ? offer.limited === decodedToken.limited
          : true;

        const matchesMaxSpeed =
          decodedToken?.address?.maxSpeed != undefined
            ? decodedToken.address.maxSpeed >= offer.downloadSpeed
            : true;

        return matchesLimited && matchesMaxSpeed;
      })
      .slice(0, 4); // added slice to not break the layout if backend returns more than two offers
  }, [
    decodedToken?.limited,
    decodedToken?.address?.maxSpeed,
    indefiniteOffers,
  ]);

  React.useLayoutEffect(() => {
    if (!decodedToken) {
      router.push(portalRoutes.sprawdzDostepnosc.index.get());
    }
  }, [decodedToken, router]);

  const [{ matches, context }, send] = useMachine(selectOfferMachine, {
    context: {
      variant,
      isLoggedIn,
      installationToken,
      currentActiveCLient,
    },
    actions: {
      onRegisterPageRedirect: ({
        orchestrator,
        selectedOfferId,
        currentActiveCLient,
      }) => {
        if (variant === "sales" && currentActiveCLient && orchestrator) {
          return redirectToOrchestratorState({
            state: orchestrator.state,
            mode: "ftth",
          });
        }

        if (selectedOfferId) {
          if (variant === "portal") {
            router.push(
              portalRoutes.kolejneKroki.get({
                oferta: selectedOfferId,
                mode: "ftth",
              }),
            );
          }
          if (variant === "sales") {
            router.push(
              portalRoutes.zalozKonto.get({
                oferta: selectedOfferId,
                mode: "ftth",
              }),
            );
          }
        }
        return;
      },
      onSuccessRedirect: ({ orchestrator }) => {
        if (orchestrator) {
          redirectToOrchestratorState({
            state: orchestrator.state,
            mode: "ftth",
          });
        }
      },
      onInProgress: ({ orchestrator }) => {
        if (variant === "sales" && orchestrator) {
          return redirectToOrchestratorState({
            state: orchestrator.state,
            mode: "ftth",
          });
        }

        router.push(portalRoutes.mojeKonto.index.get({ mode: "ftth" }));
      },
      redirectToAnotherOrderExists: () => {
        if (variant === "sales") {
          return router.push(salesRoutes.zamowienieWTrakcie.get());
        }

        return router.push(portalRoutes.zamowienieWTrakcie.get());
      },
    },
  });

  const address: IAddress | undefined = React.useMemo(() => {
    const address = getSessionAddress("installationAddress");

    return {
      street: address?.streetName,
      streetNumber: address?.houseNumber,
      municipality: address?.cityName,
      apartmentNumber: address?.apartmentNumber,
    };
  }, []);

  const handleSetOffer = React.useCallback(
    (offerId: string) => {
      send({ type: "SET_OFFER", selectedOfferId: offerId });
    },
    [send],
  );

  if (matches("error")) {
    if (variant === "sales") {
      return (
        <GeneralErrorNotification
          callback={() => {
            router.push(salesRoutes.statusKlienta.get());
          }}
          copy="powrót"
        />
      );
    }

    return (
      <GeneralErrorNotification
        callback={() => {
          router.push(portalRoutes.index.get());
        }}
      />
    );
  }

  const { selectedOfferId } = context;

  if (
    !decodedToken ||
    (variant === "sales" &&
      (matches("checkOrchestrator") || matches("checkLoggedIn")))
  ) {
    return (
      <div className="flex justify-center items-center h-full">
        <CircleLoader />
      </div>
    );
  }

  return (
    <>
      <Heading font="ttfirs" tone="neutral" variant="h2" weight="bold">
        {variant !== "sales"
          ? "wybierz ofertę dla Twojej lokalizacji"
          : "wybierz ofertę dla wskazanej lokalizacji"}
      </Heading>
      <Separator size="xs" tone="invisible" />
      {address && (
        <>
          <Text
            className="capitalize"
            size="l"
            testId="available-service-installation-location"
            tone="accent"
          >
            {formatAddress(address).toLowerCase()}
          </Text>
          <Link
            href={portalRoutes.sprawdzDostepnosc.index.get()}
            passHref={true}
          >
            <A
              data-cy="change-address"
              display="inline"
              size="inline"
              variant="simple"
            >
              <Text
                className="ml-2"
                size="m"
                weight="bold"
                withUnderline={true}
              >
                zmień adres
              </Text>
            </A>
          </Link>
          {generatedTextFromInstallationToken && (
            <Text
              align="left"
              className="mt-2"
              display="block"
              size={{
                "@onlyMobile": "xs",
                "@fromTablet": "s",
              }}
              testId="sfh-installation-info"
            >
              {generatedTextFromInstallationToken}
            </Text>
          )}
          {filteredOffers.length === 1 && (
            <BlockText
              align="left"
              display="block"
              size={{
                "@onlyMobile": "xs",
                "@fromTablet": "s",
              }}
              weight="bold"
            >
              Pod wskazanym adresem ze względu na infrastrukturę dostępna jest
              jedynie prędkość {filteredOffers[0].downloadSpeed} Mb/s.
            </BlockText>
          )}
          {filteredOffers.length === 2 && (
            <BlockText
              align="left"
              display="block"
              size={{
                "@onlyMobile": "xs",
                "@fromTablet": "s",
              }}
              weight="bold"
            >
              Pod wskazanym adresem ze względu na infrastrukturę dostępna jest
              jedynie prędkość {filteredOffers[0].downloadSpeed} Mb/s i{" "}
              {filteredOffers[1].downloadSpeed} Mb/s.
            </BlockText>
          )}
          {variant === "sales" && decodedToken && (
            <>
              <Separator size="xs" tone="invisible" />
              <BlockText testId="line-owner-info">
                Właściciel łącza: {decodedToken.address.owner}
              </BlockText>
            </>
          )}
        </>
      )}
      <div
        className={cx(
          "grid grid-flow-row from-desktop:grid-flow-col grid-cols-[1fr] gap-4 pt-8",
          {
            "from-desktop:grid-cols-[1fr_1fr_1fr_1fr]":
              filteredOffers.length === 4,
            "from-desktop:grid-cols-[1fr_1fr_1fr]": filteredOffers.length !== 4,
          },
        )}
      >
        <Offers
          isLoading={!matches("idle")}
          offers={
            variant === "portal"
              ? [...filteredOffers].reverse()
              : filteredOffers
          }
          selectedOffer={selectedOfferId}
          setOffer={handleSetOffer}
          withBadge={variant === "portal"}
        />
      </div>
    </>
  );
}
